import { useCallback, useEffect, useReducer, useState } from 'react';
import type { EffectiveChecklistItemStatus } from '../types';

export type StatusMap = Record<string, EffectiveChecklistItemStatus | undefined>;

/**
 * Ordered ids of mounted items + their latest statuses, kept together so they can't drift. Items
 * self-register (they may be wrapped — e.g. by PageModal — so `Children.toArray` won't find them);
 * `order` follows source order because React fires sibling mount effects in order. Static list only.
 */
type RegistryState = {
  order: string[];
  statuses: StatusMap;
};

type RegistryAction =
  | { type: 'upsert'; id: string; status: EffectiveChecklistItemStatus | undefined }
  | { type: 'remove'; id: string };

const EMPTY_REGISTRY: RegistryState = { order: [], statuses: {} };

function registryReducer(state: RegistryState, action: RegistryAction): RegistryState {
  switch (action.type) {
    case 'upsert': {
      const exists = state.order.includes(action.id);
      if (exists && state.statuses[action.id] === action.status) return state;
      return {
        order: exists ? state.order : [...state.order, action.id],
        statuses: { ...state.statuses, [action.id]: action.status },
      };
    }

    case 'remove': {
      if (!state.order.includes(action.id)) return state;
      const statuses = { ...state.statuses };
      delete statuses[action.id];
      return { order: state.order.filter((id) => id !== action.id), statuses };
    }
  }
}

/** First pending item at or after `fromIndex`, wrapping to the start as a fallback. */
export function nextPendingId(
  order: string[],
  statuses: StatusMap,
  fromIndex: number,
): string | null {
  return (
    order.slice(fromIndex).find((id) => statuses[id] === 'pending') ??
    order.find((id) => statuses[id] === 'pending') ??
    null
  );
}

export type ItemRegistry = {
  order: string[];
  statuses: StatusMap;
  /** Upsert an item's presence and latest status. */
  syncItem: (id: string, status: EffectiveChecklistItemStatus | undefined) => void;
  /** Drop an item from the registry on unmount. */
  removeItem: (id: string) => void;
};

/**
 * Ordered registry of step statuses. Steps self-register their status via `syncItem` (typically
 * from a mount/update effect) and drop out via `removeItem` on unmount. Shared by the checklist
 * shell and the headless step resolver so "what statuses exist, in what order" has one source.
 */
export function useItemRegistry(): ItemRegistry {
  const [{ order, statuses }, dispatch] = useReducer(registryReducer, EMPTY_REGISTRY);

  const syncItem = useCallback(
    (id: string, status: EffectiveChecklistItemStatus | undefined) =>
      dispatch({ type: 'upsert', id, status }),
    [],
  );
  const removeItem = useCallback((id: string) => dispatch({ type: 'remove', id }), []);

  return { order, statuses, syncItem, removeItem };
}

/** True once every item has mounted and reported a status; gates first paint. */
export function useSettled(order: string[], statuses: StatusMap): boolean {
  // The consumer's own mount effect runs *after* every child (item/probe) mount effect, so the
  // registry is fully populated by the time it fires — a reliable "all items registered" barrier.
  const [allItemsMounted, setAllItemsMounted] = useState<boolean>(false);
  useEffect(() => setAllItemsMounted(true), []);

  return allItemsMounted && order.every((id) => statuses[id] !== undefined);
}
